import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useParams, useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import { getApiErrors } from 'utils/errors'
import { toast } from 'react-toastify'
import { redirect } from 'utils/paths'
import APIService from 'services/APIService'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

import Modal from 'components/atoms/Modal'
import MenuItem from '@mui/material/MenuItem'
import Button from 'components/atoms/Button'
import PrimaryButton from 'components/atoms/PrimaryButton'
import styled from 'styled-components'

import FileTreeSelector from './FileTreeSelector'

const api = APIService()
const isFileEditable = (file) => (file?.is_access_editable ?? file?.isAccessEditable ?? true) === true
const isFileGranted = (file) => (file?.is_access_granted ?? file?.isAccessGranted ?? false) === true
const directPermissionsQuery = 'direct_permissions_only=1'

const ManageFilesPermissions = ({
  user,
  bp,
  onCloseMenu,
  autoOpen = false,
  showMenuItem = true
}) => {
  const { id, unitId: userToOpenId } = useParams()
  const userId = user.id
  const isOwner = user.relationType === 'owner'
  const [filesTree, setFilesTree] = useState([])
  const [open, setOpen] = useState(autoOpen)

  useBodyScrollLock(open)

  const [loading, setLoading] = useState(false)
  const [loadingCategoryIds, setLoadingCategoryIds] = useState([])
  const [selected, setSelected] = useState([])
  const selectedRef = useRef([])
  const filesTreeRef = useRef([])
  const initializedCategoryIdsRef = useRef(new Set())
  const dirtyCategoryIdsRef = useRef(new Set())
  const [dirtyCategoryIds, setDirtyCategoryIds] = useState([])
  const bulkActionRef = useRef(null)
  const [selectionBaseline, setSelectionBaseline] = useState('server')
  const selectionBaselineRef = useRef('server')
  const [changeMade, setChangeMade] = useState(false)
  const currentUser = useSelector(getCurrentUser)
  const [warningModalOpen, setWarningModalOpen] = useState(false)
  const isCurrentUserBpCreator = bp.creatorId === currentUser.id
  const isUserSelectedByUrl = userToOpenId === user.id
  const location = useLocation()
  const { pathname } = location

  const [pendingSelectAll, setPendingSelectAll] = useState(false)
  const pendingSelectAllRef = useRef(false)

  const normalizeCategory = useCallback((category) => ({
    ...category,
    fileTypes: category.fileTypes || category.file_types,
    file_types: category.fileTypes || category.file_types,
    is_loaded: category.is_loaded ?? category.isLoaded ?? true
  }), [])

  const normalizeSummaryCategory = useCallback((category) => ({
    ...category,
    fileTypes: category.fileTypes || category.file_types,
    file_types: category.fileTypes || category.file_types,
    is_loaded: false
  }), [])

  const getGrantedFileIds = useCallback((category) => {
    const grantedIds = []
    const fileTypes = Object.values(category.fileTypes || category.file_types || {})
    fileTypes.forEach(fileType => {
      fileType.files?.forEach(file => {
        if (isFileGranted(file)) grantedIds.push(file.id)
      })
    })
    return grantedIds
  }, [])

  const getAllEditableIds = useCallback((category) => {
    const ids = []
    const fileTypes = Object.values(category.fileTypes || category.file_types || {})
    fileTypes.forEach(fileType => {
      fileType.files?.forEach(file => {
        if (isFileEditable(file)) ids.push(file.id)
      })
    })
    return ids
  }, [])

  const handleSelectionChange = useCallback((newSelected) => {
    selectedRef.current = newSelected
    setSelected(newSelected)
    setChangeMade(true)
  }, [])

  const markSelectionIntent = useCallback((categoryId) => {
    bulkActionRef.current = null

    if (categoryId) {
      dirtyCategoryIdsRef.current.add(categoryId)
      setDirtyCategoryIds(Array.from(dirtyCategoryIdsRef.current))
    }
    setChangeMade(true)
  }, [])

  const deactivatePendingSelectAll = useCallback(() => {
    if (pendingSelectAllRef.current) {
      pendingSelectAllRef.current = false
      setPendingSelectAll(false)
    }
  }, [])

  const getSelectedSnapshot = useCallback(() => selectedRef.current, [])

  const resetState = useCallback(() => {
    setFilesTree([])
    setLoadingCategoryIds([])
    setSelected([])
    selectedRef.current = []
    filesTreeRef.current = []
    initializedCategoryIdsRef.current = new Set()
    dirtyCategoryIdsRef.current = new Set()
    setDirtyCategoryIds([])
    bulkActionRef.current = null
    selectionBaselineRef.current = 'server'
    setSelectionBaseline('server')
    setPendingSelectAll(false)
    pendingSelectAllRef.current = false
    setChangeMade(false)
    setWarningModalOpen(false)
  }, [])

  const handleOpen = () => {
    resetState()
    setOpen(true)
    if (showMenuItem) {
      onCloseMenu?.()
    }
  }

  useEffect(() => {
    if (!autoOpen) return

    resetState()
    setOpen(true)
  }, [autoOpen, resetState])

  const handleClose = () => {
    if (changeMade) {
      setWarningModalOpen(true)
    } else {
      if (isUserSelectedByUrl) {
        redirect(pathname.replace(`/${user.id}`, ''))
      }
      resetState()
      setOpen(false)
      onCloseMenu?.()
    }
  }

  const mergeCategoryIntoTree = useCallback((incomingCategory, { initializeSelection = true } = {}) => {
    const normalizedCategory = normalizeCategory(incomingCategory)

    setFilesTree(prev => {
      const nextTree = [...prev]
      const existingIndex = nextTree.findIndex(c => c.id === normalizedCategory.id)

      if (existingIndex >= 0) {
        nextTree[existingIndex] = { ...nextTree[existingIndex], ...normalizedCategory }
      } else {
        nextTree.push(normalizedCategory)
      }

      filesTreeRef.current = nextTree
      return nextTree
    })

    if (initializeSelection && !initializedCategoryIdsRef.current.has(normalizedCategory.id)) {
      let nextIds = []

      if (pendingSelectAllRef.current || selectionBaselineRef.current === 'all') {
        nextIds = getAllEditableIds(normalizedCategory)
      } else if (selectionBaselineRef.current === 'server') {
        nextIds = getGrantedFileIds(normalizedCategory)
      }

      if (nextIds.length > 0) {
        setSelected(currentSelected => {
          const nextSelected = Array.from(new Set([...currentSelected, ...nextIds]))
          selectedRef.current = nextSelected
          return nextSelected
        })
      }
    }

    initializedCategoryIdsRef.current.add(normalizedCategory.id)

    return normalizedCategory
  }, [getAllEditableIds, getGrantedFileIds, normalizeCategory])

  const loadCategoryFiles = useCallback((categoryId) => {
    const targetCategory = filesTreeRef.current.find(c => c.id === categoryId)
    if (!targetCategory || targetCategory.is_loaded) {
      return Promise.resolve(targetCategory)
    }

    setLoadingCategoryIds(prev => [...prev, categoryId])

    return api.get(`/building-passport/${id}/files-tree?user_id=${userId}&${directPermissionsQuery}&category_id=${categoryId}`)
      .then(resp => {
        const data = resp?.data?.data || []
        const category = Array.isArray(data) ? data[0] : Object.values(data)[0]
        if (category) {
          return mergeCategoryIntoTree(category, {
            initializeSelection: !dirtyCategoryIdsRef.current.has(categoryId)
          })
        }
        return null
      })
      .catch(err => {
        toast.error(getApiErrors(err))
        return null
      })
      .finally(() => {
        setLoadingCategoryIds(prev => prev.filter(currentId => currentId !== categoryId))
      })
  }, [id, mergeCategoryIntoTree, userId])

  const loadCategoryFilesBatch = useCallback((categoryIds, { initializeSelection = true } = {}) => {
    const targetCategoryIds = categoryIds.filter(categoryId => {
      const category = filesTreeRef.current.find(item => item.id === categoryId)
      return category && category.is_loaded !== true
    })

    if (targetCategoryIds.length === 0) {
      return Promise.resolve([])
    }

    setLoadingCategoryIds(prev => Array.from(new Set([...prev, ...targetCategoryIds])))

    return api.get(`/building-passport/${id}/files-tree?user_id=${userId}&${directPermissionsQuery}&category_ids=${targetCategoryIds.join(',')}`)
      .then(resp => {
        const data = resp?.data?.data || []
        const categories = Array.isArray(data) ? data : Object.values(data)
        return categories.map(category => mergeCategoryIntoTree(category, {
          initializeSelection: initializeSelection && !dirtyCategoryIdsRef.current.has(category.id)
        }))
      })
      .catch(err => {
        toast.error(getApiErrors(err))
        return []
      })
      .finally(() => {
        setLoadingCategoryIds(prev => prev.filter(currentId => !targetCategoryIds.includes(currentId)))
      })
  }, [id, mergeCategoryIntoTree, userId])

  const handleCategoryExpand = useCallback((categoryId) => {
    return loadCategoryFiles(categoryId)
  }, [loadCategoryFiles])

  const fetchFilesTreeSummary = useCallback(() => {
    setLoading(true)
    setSelected([])
    selectedRef.current = []
    setChangeMade(false)
    initializedCategoryIdsRef.current = new Set()
    dirtyCategoryIdsRef.current = new Set()
    setDirtyCategoryIds([])
    bulkActionRef.current = null
    selectionBaselineRef.current = 'server'
    setSelectionBaseline('server')

    api.get(`/building-passport/${id}/files-tree?user_id=${userId}&${directPermissionsQuery}&include_files=0`)
      .then(resp => {
        const fileCategories = Object.values(resp?.data?.data || {}).map(normalizeSummaryCategory)
        setFilesTree(fileCategories)
        filesTreeRef.current = fileCategories
        setLoading(false)
      })
      .catch(err => {
        toast.error(getApiErrors(err))
        setLoading(false)
      })
  }, [id, normalizeSummaryCategory, userId])

  useEffect(() => {
    if (userId && open) {
      fetchFilesTreeSummary()
    } else {
      resetState()
    }
  }, [open, userId, fetchFilesTreeSummary, resetState])

  const handleSubmit = () => {
    const currentBulkAction = bulkActionRef.current

    if (currentBulkAction) {
      api.patch(`/building-passport/${id}/files-access`, { user_id: userId, bulk_action: currentBulkAction })
        .then(() => {
          if (isUserSelectedByUrl) {
            redirect(pathname.replace(`/${user.id}`, ''))
          }
          resetState()
          setOpen(false)
          onCloseMenu?.()
          toast.success('Permissions saved')
        })
        .catch(err => {
          toast.error(getApiErrors(err))
        })

      return
    }

    const dirtyCategoryIds = Array.from(dirtyCategoryIdsRef.current)
    const baseline = selectionBaselineRef.current
    const currentSelectedSet = new Set(selectedRef.current)

    loadCategoryFilesBatch(dirtyCategoryIds, { initializeSelection: false })
      .then(() => {
        const loadedDirtyCategories = filesTreeRef.current.filter(category =>
          dirtyCategoryIdsRef.current.has(category.id) && category.is_loaded !== false
        )

        const filesToGrant = []
        const filesToRevoke = []

        loadedDirtyCategories.forEach(category => {
          const editableFileIds = getAllEditableIds(category)

          if (baseline === 'all') {
            editableFileIds.forEach(fileId => {
              if (!currentSelectedSet.has(fileId)) {
                filesToRevoke.push(fileId)
              }
            })
            return
          }

          if (baseline === 'none') {
            editableFileIds.forEach(fileId => {
              if (currentSelectedSet.has(fileId)) {
                filesToGrant.push(fileId)
              }
            })
            return
          }

          const grantedSet = new Set(getGrantedFileIds(category))
          editableFileIds.forEach(fileId => {
            const isSelected = currentSelectedSet.has(fileId)
            const wasGranted = grantedSet.has(fileId)

            if (isSelected && !wasGranted) {
              filesToGrant.push(fileId)
            }

            if (!isSelected && wasGranted) {
              filesToRevoke.push(fileId)
            }
          })
        })

        if (baseline === 'all') {
          return api.patch(`/building-passport/${id}/files-access`, {
            user_id: userId,
            bulk_action: 'grant_all_editable'
          }).then(() => {
            if (filesToRevoke.length === 0) {
              return null
            }

            return api.patch(`/building-passport/${id}/files-access`, {
              user_id: userId,
              files: filesToRevoke.map(fileId => ({ id: fileId, permission: false }))
            })
          })
        }

        if (baseline === 'none') {
          return api.patch(`/building-passport/${id}/files-access`, {
            user_id: userId,
            bulk_action: 'revoke_all_editable'
          }).then(() => {
            if (filesToGrant.length === 0) {
              return null
            }

            return api.patch(`/building-passport/${id}/files-access`, {
              user_id: userId,
              files: filesToGrant.map(fileId => ({ id: fileId, permission: true }))
            })
          })
        }

        if (filesToGrant.length === 0 && filesToRevoke.length === 0) {
          return null
        }

        return api.patch(`/building-passport/${id}/files-access`, {
          user_id: userId,
          files: [
            ...filesToGrant.map(fileId => ({ id: fileId, permission: true })),
            ...filesToRevoke.map(fileId => ({ id: fileId, permission: false }))
          ]
        })
      })
      .then(() => {
        if (isUserSelectedByUrl) {
          redirect(pathname.replace(`/${user.id}`, ''))
        }
        resetState()
        setOpen(false)
        onCloseMenu?.()
        toast.success('Permissions saved')
      })
      .catch(err => {
        toast.error(getApiErrors(err))
      })
  }

  const handleWarningModalClose = () => {
    if (isUserSelectedByUrl) {
      redirect(pathname.replace(`/${user.id}`, ''))
    }
    resetState()
    setOpen(false)
    onCloseMenu?.()
  }

  const allEditable = useMemo(() => {
    if (pendingSelectAll) return true
    if (filesTree.length === 0) return false

    return filesTree.every(category => {
      const fileTypes = Object.values(category.fileTypes || category.file_types || {})

      if (category.is_loaded !== false) {
        let totalAll = 0
        let selectedCount = 0
        for (const type of fileTypes) {
          for (const file of type.files || []) {
            totalAll++
            if (selected.includes(file.id)) selectedCount++
          }
        }
        if (totalAll === 0) return true
        return selectedCount === totalAll
      }

      const editableCount = fileTypes.reduce((acc, t) => acc + (t?.editable_files_count ?? t?.editableFilesCount ?? 0), 0)

      if (selectionBaseline === 'none' && !dirtyCategoryIdsRef.current.has(category.id)) {
        return false
      }

      if (selectionBaseline === 'all' && !dirtyCategoryIdsRef.current.has(category.id)) {
        return editableCount > 0
      }

      if (dirtyCategoryIdsRef.current.has(category.id)) {
        return false
      }

      const grantedCount = fileTypes.reduce((acc, t) => {
        const g = t?.granted_files_count ?? t?.grantedFilesCount ?? 0
        const e = t?.editable_files_count ?? t?.editableFilesCount ?? 0
        return acc + Math.min(g, e)
      }, 0)
      const totalGrantedCount = fileTypes.reduce((acc, t) => acc + (t?.granted_files_count ?? t?.grantedFilesCount ?? 0), 0)
      const hasGrantedButNotEditable = editableCount === 0 && totalGrantedCount > 0

      return hasGrantedButNotEditable || (grantedCount === editableCount && editableCount > 0)
    })
  }, [filesTree, selected, pendingSelectAll, dirtyCategoryIds, selectionBaseline])

  const handleSelectDeselectAll = useCallback(() => {
    setChangeMade(true)
    dirtyCategoryIdsRef.current = new Set()
    setDirtyCategoryIds([])

    if (allEditable || pendingSelectAll) {
      bulkActionRef.current = 'revoke_all_editable'
      selectionBaselineRef.current = 'none'
      setSelectionBaseline('none')
      pendingSelectAllRef.current = false
      setPendingSelectAll(false)
      selectedRef.current = []
      setSelected([])
    } else {
      bulkActionRef.current = 'grant_all_editable'
      selectionBaselineRef.current = 'all'
      setSelectionBaseline('all')
      pendingSelectAllRef.current = true
      setPendingSelectAll(true)

      // Immediately select files that are already in memory
      const editableIds = []
      filesTreeRef.current.forEach(category => {
        if (category.is_loaded !== false) {
          Object.values(category.fileTypes || category.file_types || {}).forEach(type => {
            type.files?.forEach(file => {
              if (isFileEditable(file)) editableIds.push(file.id)
            })
          })
        }
      })
      selectedRef.current = editableIds
      setSelected(editableIds)
    }
  }, [allEditable, pendingSelectAll])

  return (
    <div>
      {showMenuItem && (
        <MenuItem
          onClick={() => {
            if (userId === currentUser.id) {
              toast.info('You cannot change your own access to files.')
              onCloseMenu?.()
            } else if (isOwner && !isCurrentUserBpCreator) {
              toast.info('You do not have permission to change file access for Admin users.')
              onCloseMenu?.()
            } else {
              handleOpen()
            }
          }}
        >
          Manage permissions
        </MenuItem>
      )}

      <Modal
        onClose={handleClose}
        open={open}
        title='Manage file permissions'
      >
        <ResponsiveContainer>
          <FileTreeSelector
            fileTree={filesTree}
            selected={selected}
            getSelectedSnapshot={getSelectedSnapshot}
            onSelectionChange={handleSelectionChange}
            onSelectionIntent={markSelectionIntent}
            onPendingSelectAllDeactivate={deactivatePendingSelectAll}
            loading={loading && filesTree.length === 0}
            loadingCategoryIds={loadingCategoryIds}
            onCategoryExpand={handleCategoryExpand}
            pendingSelectAll={pendingSelectAll}
            dirtyCategoryIds={dirtyCategoryIds}
            selectionBaseline={selectionBaseline}
          />

          <StickyFooter>
            <FooterLeft>
              <Button
                variant='text'
                onClick={handleSelectDeselectAll}
              >
                {allEditable || pendingSelectAll ? 'Deselect All' : 'Select All'}
              </Button>
            </FooterLeft>
            <FooterRight>
              <Button onClick={handleClose}>Cancel</Button>
              <PrimaryButton onClick={handleSubmit} disabled={!changeMade}>Save</PrimaryButton>
            </FooterRight>
          </StickyFooter>
        </ResponsiveContainer>

        <Modal
          open={warningModalOpen}
          title='Unsaved changes'
          onClose={handleWarningModalClose}
        >
          <div style={{ padding: '0 0 20px' }}>
            You have unsaved changes. Do you want to save them before closing?
          </div>
          <ButtonsWrapper>
            <Button onClick={handleWarningModalClose}>Discard</Button>
            <PrimaryButton onClick={handleSubmit}>Save Changes</PrimaryButton>
          </ButtonsWrapper>
        </Modal>
      </Modal>
    </div>
  )
}

export default ManageFilesPermissions

const ResponsiveContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 600px;
  height: 70vh;
  max-height: 800px;
  background: white;

  @media (max-width: 768px) {
    width: 100%;
    height: 100vh;
    max-height: none;
    border-radius: 0;
  }
`

const StickyFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: row;
    gap: 12px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
`

const FooterLeft = styled.div`
  @media (max-width: 480px) {
    display: flex;

    button {
      min-width: 0;
      padding-left: 0;
      padding-right: 0;
      justify-content: flex-start;
    }
  }
`

const FooterRight = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 480px) {
    width: auto;

    button {
      justify-content: center;
    }
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  margin-top: 20px;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
`
